import _ from 'lodash';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { LoadSaveService } from 'twia-capability-quoteandbind-residential';
import { GatewayRenewalService as RenewalLoadSaveService} from 'twia-capability-gateway-policyrenewal';
import { GatewayEndorsementService as EndorsementLoadSaveService } from 'twia-capability-gateway-policychange';
import QuoteExitPopupUtil from './QuoteExitPopupUtil';
import TWIAJobHelper from 'ent-capability-quoteandbind-common-react/util/TWIAJobHelper';


// 2024-12-13 - Robert Grey - DE17579 - Kufri Regression_Portals_PE: Error occurred while submitting an offer with Payment method =
//                                      EFT or E-check and upon re-submitting offer it keeps on processing 
// Created to use the new Cancel action added to the Jutro Wizard package in the Kufri version to customize save and exit handling 

// 2024-12-20 - Debra Frum  - US29205 - PE - Renewal - Common - Cancel Quote Pop-up Message (Handles Commercial and Residential Both)
// Refactored for use with Renewals in addition to Submissions

// 2024-12-29 - Debra Frum - US29286 - PE - Common - CANCEL PREVIOUS NEXT SAVE&EXIT - Part 2
// Handle Policy Changes

const push = (history, path, response, authHeader) => {
    return history.push({
        pathname: path,
        state: {
            response,
            authHeader
        },
    });
}
 
const boundAndSubmittedStatusCodes = ['Bound', 'Submitted_Ext', 'NotTaken', 'Declined', 'Expired', 'Withdrawn'];

const isStatusUndefined = (wizardData) => {
    const periodStatus = _.get(wizardData.value, 'baseData.periodStatus') ;
    const status = _.get(wizardData.value, 'status') 
    return _.isNil(periodStatus) && _.isNil(status);
}

const isBoundOrSubmitted = (wizardData) => {
    const periodStatus = _.get(wizardData.value, 'baseData.periodStatus'); 
    const status = _.get(wizardData.value, 'status') 
    return _.includes(boundAndSubmittedStatusCodes, periodStatus)
        || _.includes(boundAndSubmittedStatusCodes, status);
}

const getServerCallObj = (wizardData, payload) => {
    const periodStatus = _.get(wizardData.value, 'baseData.periodStatus') 
    || _.get(wizardData.value, 'status');
    const isStatusQuoted = periodStatus === 'Quoted';
    const isStatusRenewing = periodStatus === 'Renewing';

    const status = _.get(wizardData.value, 'status');

    let serverCallObj = {};
    // 2024-12-29 - Debra Frum - DE17673 - Don't display the Exit confirmation popup when Submit button clicked on Payment Details page
    // Suppress saving transaction when exiting wizard after Submit button clicked on Payment Details page since payment has already been saved

    if (!isStatusUndefined(wizardData) 
    &&  !isBoundOrSubmitted(wizardData)) {
        if (TWIAJobHelper.isRenewal(wizardData.value)) {
            serverCallObj = {
                serverCall   : isStatusQuoted || isStatusRenewing ? 
                            RenewalLoadSaveService.updateAndForgetQuotedRenewal : 
                            RenewalLoadSaveService.saveAndForgetRenewal,
                payloadParam : [payload]
            }
        } else if (TWIAJobHelper.isPolicyChange(wizardData.value)) {
            serverCallObj = {
                serverCall   : isStatusQuoted ? 
                            EndorsementLoadSaveService.saveAndForgetQuotedEndorsement : 
                            EndorsementLoadSaveService.saveAndForgetEndorsement,
                payloadParam : payload
            }
        } else {
            serverCallObj = {
                serverCall   : isStatusQuoted ? 
                            LoadSaveService.updateAndForgetQuotedSubmission : 
                            LoadSaveService.updateAndForgetDraftSubmission,
                payloadParam : payload
            }
        }
    }

    return serverCallObj;
}

const saveAndExit = (wizardData, wizardSnapshot, setIsLoading, history, redirectPath, authHeader) => {
    const isHomeowners = _.get(wizardData.value, 'baseData.productCode') === 'Homeowners';
    const isTWIA = _.get(wizardData.value, 'baseData.associationName_TWIA') === 'twia'

    // DE17653 - ProducerEngage - TFPA submit and exit paths for quote wizard
    // 2024-12-29 - Debra Frum - DE17673 - Don't display the Exit confirmation popup when Submit button clicked on Payment Details page
    //   If payment has just been submitted on transaction, display the Payment Confirmation page.
    //   Otherwise payment has been updated on previously submitted transactions so display the Transaction Summary page.
    if(isBoundOrSubmitted(wizardData)){
        // eslint-disable-next-line no-param-reassign
        wizardData.noCancelWarning = true;

        let redirectPathNew = redirectPath;

        // DE17858
        if(redirectPathNew.includes('Confirmation')){
            if(isTWIA){
                if (TWIAJobHelper.isSubmission(wizardData.value)) {
                    redirectPathNew = isHomeowners ? '/twiaResConfirmation' : '/quoteConfirmation'
                } else if (TWIAJobHelper.isRenewal(wizardData.value)) {
                    redirectPathNew = '/renewalConfirmation'
                } else if (TWIAJobHelper.isPolicyChange(wizardData.value)) {
                    redirectPathNew = '/endorsementConfirmation'
                }
    
            }else{
                redirectPathNew = '/tfpaResConfirmation'
            }
        }

        push(history, redirectPathNew, wizardData.value, authHeader)

        return;
    }

    const aspects = wizardData.lobData.aspects;
    const payload = aspects.valid && aspects.subtreeValid
        ? _.get(wizardData, 'value')
        : _.get(wizardSnapshot, 'value')

        if(isTWIA){
            _.set(payload, 'bindData.paymentDetails.electronicPaymentData_TWIA', undefined);
        }

    setIsLoading(true);

    const serverCallObj = getServerCallObj(wizardData, payload);
    if (Object.keys(serverCallObj).length === 0) {
        push(history, redirectPath, wizardData.value, authHeader)
    } else {
        const {serverCall, payloadParam} = serverCallObj;
        return serverCall(payloadParam, authHeader)
            .then(() => {
                push(history, redirectPath, wizardData.value, authHeader)
            }).catch(() => {
                push(history, redirectPath, wizardData.value, authHeader)
            })
    }
}

const handleCancel = (props) => {
    const { wizardSnapshot, wizardData, showConfirm, translator, wizardMessages, setIsLoading, history, 
        redirectPath, authHeader } = props;

    if (!wizardData.noCancelWarning && !redirectPath.includes('Confirmation')) {
        const confirmMessage =  QuoteExitPopupUtil.getConfirmMessage(wizardData, translator);

        showConfirm({
            title: wizardMessages.TWIAWizardModalCancelTitle,
            message: confirmMessage,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.yesModel,
            cancelButtonText: commonMessages.noModel,
        }).then((results) => {
            if (results === 'cancel' || results === 'close') {
                return _.noop();
            } else {
                const isTWIA = _.get(wizardData.value, 'baseData.associationName_TWIA') === 'twia';
                const isSubmissionOrRenewal = ['Submission', 'Renewal'].includes(_.get(wizardData.value, 'baseData.jobType'));
                if (isTWIA && isSubmissionOrRenewal && history?.location?.pathname?.includes('paymentdetails')) {
                    const bindDataVM = _.get(wizardSnapshot, 'bindData.value');
                    _.set(wizardData, 'bindData.value', bindDataVM);
                }
                saveAndExit(wizardData, wizardSnapshot, setIsLoading, history, redirectPath, authHeader);
            }
        }, _.noop);
    } else {
        saveAndExit(wizardData, wizardSnapshot, setIsLoading, history, redirectPath, authHeader);
    }
}

const skipStep = (async (submissionVM, initialValidation) => {
    const ppStatus = _.get(submissionVM.value, 'baseData.periodStatus') 
    || _.get(submissionVM.value, 'status');

    return (await initialValidation()) && ppStatus === 'Quoted';
});

export default { 
    'handleCancel': handleCancel,
    'getServerCallObj': getServerCallObj,
    'skipStep': skipStep
}
